/* The Face Tonic — shared client-side behaviour.
   - Scroll reveal for .tft-reveal elements
   - Sticky-shrink header
   - Floating "Book Now" FAB visibility
   - Heart / save-for-later (localStorage)
   - Mobile saved-count indicator
*/
(function () {
  'use strict';

  /* ── Scroll reveal ───────────────────────────────────────────────── */
  const reveals = document.querySelectorAll('.tft-reveal');
  if (reveals.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    reveals.forEach((el) => io.observe(el));
  } else {
    reveals.forEach((el) => el.classList.add('is-in'));
  }

  /* ── Sticky-shrink header (hysteresis to prevent shake) ──────────── */
  let _stuckTicking = false;
  let _isStuck = false;
  const onScroll = () => {
    if (_stuckTicking) return;
    _stuckTicking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY || document.documentElement.scrollTop;
      const next = _isStuck ? y > 60 : y > 100;
      if (next !== _isStuck) {
        _isStuck = next;
        document.body.classList.toggle('tft-stuck', next);
      }
      document.body.classList.toggle('tft-fab-shown', y > 480);
      var bar = document.querySelector('[data-tft-stickybar]');
      if (bar) bar.classList.toggle('is-shown', y > 480);
      _stuckTicking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ── Heart / save-for-later ───────────────────────────────────────── */
  const STORAGE_KEY = 'tft-favourites';
  function getFaves() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch (e) { return []; }
  }
  function setFaves(arr) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); }
    catch (e) {}
    updateSavedCount();
  }
  function isFave(id) { return getFaves().includes(id); }
  function toggleFave(id) {
    const faves = getFaves();
    const idx = faves.indexOf(id);
    if (idx >= 0) faves.splice(idx, 1);
    else faves.push(id);
    setFaves(faves);
    return faves.includes(id);
  }
  function updateSavedCount() {
    const count = getFaves().length;
    document.querySelectorAll('[data-tft-saved-count]').forEach((el) => {
      el.textContent = count > 0 ? '· ' + count : '';
    });
  }

  // Initial paint of heart states
  document.querySelectorAll('[data-tft-favourable]').forEach((card) => {
    const id = card.dataset.tftId;
    if (isFave(id)) card.classList.add('is-faved');
  });

  // Click delegation for hearts
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tft-fav-btn]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const card = btn.closest('[data-tft-favourable]');
    if (!card) return;
    const id = card.dataset.tftId;
    const faved = toggleFave(id);
    card.classList.toggle('is-faved', faved);
    btn.setAttribute('aria-label', faved ? 'Remove from favourites' : 'Save to favourites');
  });

  updateSavedCount();

  /* ── Quick Add to cart (AJAX) ─────────────────────────────────── */
  function flashLabel(btn, msg, ok) {
    var label = btn.querySelector('[data-tft-quickadd-label]') || btn;
    var orig = label.textContent;
    label.textContent = msg;
    btn.classList.toggle('is-ok', !!ok);
    btn.classList.toggle('is-error', !ok);
    setTimeout(function () {
      label.textContent = orig;
      btn.classList.remove('is-ok', 'is-error');
    }, 2000);
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-tft-quickadd]');
    if (!form) return;
    e.preventDefault();
    var btn = form.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form),
    })
      .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
      .then(function (res) {
        if (!res.ok) {
          flashLabel(btn, (res.body && res.body.description) || 'Try again', false);
        } else {
          flashLabel(btn, 'Added ✓', true);
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.querySelectorAll('.cart-count-bubble, [data-cart-count]').forEach(function (el) {
                var num = el.querySelector('span') || el;
                num.textContent = cart.item_count;
                if (cart.item_count > 0) el.removeAttribute('hidden');
              });
              document.dispatchEvent(new CustomEvent('tft:cart-updated', { detail: cart }));
            })
            .catch(function () {});
        }
      })
      .catch(function () { flashLabel(btn, 'Try again', false); })
      .finally(function () { if (btn) btn.disabled = false; });
  });

  /* ── Hero treatment search ────────────────────────────────────────── */
  const searchEl = document.querySelector('[data-tft-search]');
  const dataEl = document.getElementById('tft-treatments-data');
  if (searchEl && dataEl) {
    let DATA = [];
    try { DATA = JSON.parse(dataEl.textContent); } catch (e) {}
    const input = searchEl.querySelector('[data-tft-search-input]');
    const results = searchEl.querySelector('[data-tft-search-results]');
    const clearBtn = searchEl.querySelector('[data-tft-search-clear]');
    let activeIdx = -1;
    let lastResults = [];

    const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));

    const highlight = (name, q) => {
      if (!q) return escapeHtml(name);
      const safeQ = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return escapeHtml(name).replace(new RegExp('(' + safeQ + ')', 'ig'), '<mark>$1</mark>');
    };

    const score = (t, q) => {
      const k = t.k || '';
      const n = t.n.toLowerCase();
      if (n === q) return 1000;
      if (n.startsWith(q)) return 500 + (50 - n.length);
      if (n.includes(q)) return 200 + (50 - n.length);
      if (k.includes(q)) return 50;
      return 0;
    };

    const render = (items, q) => {
      if (!items.length) {
        results.innerHTML = '<div class="tft-hero__search-empty">No treatment matched. <a href="/pages/treatments">See the full list →</a></div>';
        results.hidden = false;
        return;
      }
      results.innerHTML = items.map((t, i) => `
        <a class="tft-hero__search-result${i === activeIdx ? ' is-active' : ''}" href="${t.u}" role="option" data-idx="${i}">
          <span class="tft-hero__search-result-name">${highlight(t.n, q)}</span>
          <span class="tft-hero__search-result-price">${escapeHtml(t.p || '')}</span>
          <span class="tft-hero__search-result-meta"><b>${escapeHtml(t.c)}</b>${t.d ? ' · ' + escapeHtml(t.d) : ''}</span>
        </a>
      `).join('');
      results.hidden = false;
    };

    const onInput = () => {
      const q = input.value.trim().toLowerCase();
      activeIdx = -1;
      clearBtn.hidden = !q;
      if (q.length < 2) { results.hidden = true; lastResults = []; return; }
      const hits = DATA
        .map((t) => ({ t, s: score(t, q) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s)
        .slice(0, 8)
        .map((x) => x.t);
      lastResults = hits;
      render(hits, q);
    };

    input.addEventListener('input', onInput);
    input.addEventListener('focus', () => { if (lastResults.length) results.hidden = false; });

    input.addEventListener('keydown', (e) => {
      if (results.hidden) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIdx = Math.min(activeIdx + 1, lastResults.length - 1);
        render(lastResults, input.value.trim().toLowerCase());
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIdx = Math.max(activeIdx - 1, 0);
        render(lastResults, input.value.trim().toLowerCase());
      } else if (e.key === 'Enter' && activeIdx >= 0) {
        e.preventDefault();
        const t = lastResults[activeIdx];
        if (t) window.location.href = t.u;
      } else if (e.key === 'Escape') {
        results.hidden = true;
      }
    });

    clearBtn.addEventListener('click', () => {
      input.value = '';
      results.hidden = true;
      clearBtn.hidden = true;
      input.focus();
    });

    document.addEventListener('click', (e) => {
      if (!searchEl.contains(e.target)) results.hidden = true;
    });
  }

  /* ── Footer accordion (mobile only — desktop CSS overrides) ──────── */
  document.querySelectorAll('[data-tft-acc] .tft-acc__toggle').forEach((btn) => {
    btn.addEventListener('click', () => {
      const col = btn.closest('[data-tft-acc]');
      if (!col) return;
      const open = col.hasAttribute('data-open');
      if (open) {
        col.removeAttribute('data-open');
        btn.setAttribute('aria-expanded', 'false');
      } else {
        col.setAttribute('data-open', '');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  });
})();
